/**
 * Content Manager - Configuration Store
 *
 * Zustand store for managing application configuration and settings.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AppConfig, PlatformBrand, CsvFieldMapping, LoggingConfig, LogLevel, IntegrationCredentials } from '../types';
import { DEFAULT_APP_CONFIG, DEFAULT_LOGGING_CONFIG, DEFAULT_CREDENTIALS } from '../constants';
import { logger } from '../lib/logger';
import { configService } from '../services/config';

// =============================================================================
// TYPES
// =============================================================================

interface ConfigState {
  // Configuration
  config: AppConfig;
  isLoading: boolean;
  error: string | null;

  // Convenience accessors (derived from config)
  accentColour: string;

  // Actions
  fetchConfig: () => Promise<void>;
  loadConfig: (config: AppConfig) => void;
  saveConfig: (config: AppConfig) => Promise<void>;
  updateConfig: (updates: Partial<AppConfig>) => Promise<void>;
  resetConfig: () => Promise<void>;

  // Specific config actions
  setAccentColour: (colour: string) => Promise<void>;
  setTheme: (theme: 'light' | 'dark') => Promise<void>;
  togglePerformanceTab: () => Promise<void>;

  // Content types
  addContentType: (type: string) => Promise<void>;
  removeContentType: (type: string) => Promise<void>;
  reorderContentTypes: (types: string[]) => Promise<void>;

  // Platforms
  addPlatform: (platform: string) => Promise<void>;
  removePlatform: (platform: string) => Promise<void>;
  updatePlatformBrand: (platform: string, brand: PlatformBrand) => Promise<void>;

  // Silos
  addSilo: (silo: string) => Promise<void>;
  removeSilo: (silo: string) => Promise<void>;
  reorderSilos: (silos: string[]) => Promise<void>;

  // CSV mapping
  updateCsvFieldMapping: (mapping: CsvFieldMapping) => Promise<void>;

  // Logging
  updateLoggingConfig: (config: Partial<LoggingConfig>) => Promise<void>;
  setLoggingEnabled: (enabled: boolean) => Promise<void>;
  setLogLevel: (level: LogLevel) => Promise<void>;
  toggleLoggingCategory: (category: keyof LoggingConfig['categories']) => Promise<void>;
  resetLoggingConfig: () => Promise<void>;

  // Credentials
  updateCredentials: (credentials: Partial<IntegrationCredentials>) => Promise<void>;
  setAhrefsApiKey: (key: string | null) => Promise<void>;
  setClaudeApiKey: (key: string | null) => Promise<void>;
  setGaPropertyId: (propertyId: string | null) => Promise<void>;
  setGscSiteUrl: (siteUrl: string | null) => Promise<void>;
  clearAllCredentials: () => Promise<void>;

  // Loading/Error
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

// =============================================================================
// STORE
// =============================================================================

export const useConfigStore = create<ConfigState>()(
  persist(
    (set, get) => ({
      // Initial state
      config: DEFAULT_APP_CONFIG,
      isLoading: false,
      error: null,

      // Convenience accessor (synced with config)
      get accentColour() {
        return get().config.accentColour;
      },

      // Fetch configuration from API
      fetchConfig: async () => {
        set({ isLoading: true, error: null });
        try {
          const { data, error } = await configService.fetch();
          if (error) {
            set({ error: error.message, isLoading: false });
            return;
          }
          if (data) {
            set({ config: data, isLoading: false, error: null });
            // Sync logger with fetched config
            if (data.logging) {
              logger.setConfig(data.logging);
            }
          }
        } catch (err) {
          set({ error: (err as Error).message, isLoading: false });
        }
      },

      // Load configuration (from local source)
      loadConfig: (config) => {
        set({ config, error: null });
      },

      // Save configuration (persists to API)
      saveConfig: async (config) => {
        const newConfig = { ...config, updatedAt: new Date().toISOString() };
        set({ config: newConfig, error: null });
        // Persist to backend
        await configService.update(newConfig);
      },

      // Update configuration partially (persists to API)
      updateConfig: async (updates) => {
        const state = get();
        const newConfig = {
          ...state.config,
          ...updates,
          updatedAt: new Date().toISOString(),
        };
        set({ config: newConfig });
        // Persist to backend
        await configService.update(updates);
      },

      // Reset to default configuration (persists to API)
      resetConfig: async () => {
        set({ config: DEFAULT_APP_CONFIG, error: null });
        // Persist to backend
        await configService.reset();
      },

      // Set accent colour
      setAccentColour: async (colour) => {
        await get().updateConfig({ accentColour: colour });
      },

      // Set theme
      setTheme: async (theme) => {
        await get().updateConfig({ theme });
      },

      // Toggle performance tab visibility
      togglePerformanceTab: async () => {
        const state = get();
        await get().updateConfig({ hidePerformanceTab: !state.config.hidePerformanceTab });
      },

      // ==========================================================================
      // CONTENT TYPES
      // ==========================================================================

      addContentType: async (type) => {
        const state = get();
        if (state.config.contentTypes.includes(type)) return;
        await get().updateConfig({ contentTypes: [...state.config.contentTypes, type] });
      },

      removeContentType: async (type) => {
        const state = get();
        await get().updateConfig({ contentTypes: state.config.contentTypes.filter((t) => t !== type) });
      },

      reorderContentTypes: async (types) => {
        await get().updateConfig({ contentTypes: types });
      },

      // ==========================================================================
      // PLATFORMS
      // ==========================================================================

      addPlatform: async (platform) => {
        const state = get();
        if (state.config.platforms.includes(platform)) return;
        await get().updateConfig({ platforms: [...state.config.platforms, platform] });
      },

      removePlatform: async (platform) => {
        const state = get();
        const { [platform]: _, ...remainingBrands } = state.config.platformBrands;
        await get().updateConfig({
          platforms: state.config.platforms.filter((p) => p !== platform),
          platformBrands: remainingBrands,
        });
      },

      updatePlatformBrand: async (platform, brand) => {
        const state = get();
        await get().updateConfig({
          platformBrands: {
            ...state.config.platformBrands,
            [platform]: brand,
          },
        });
      },

      // ==========================================================================
      // SILOS
      // ==========================================================================

      addSilo: async (silo) => {
        const state = get();
        if (state.config.contentSilos.includes(silo)) return;
        await get().updateConfig({ contentSilos: [...state.config.contentSilos, silo] });
      },

      removeSilo: async (silo) => {
        const state = get();
        await get().updateConfig({ contentSilos: state.config.contentSilos.filter((s) => s !== silo) });
      },

      reorderSilos: async (silos) => {
        await get().updateConfig({ contentSilos: silos });
      },

      // ==========================================================================
      // CSV MAPPING
      // ==========================================================================

      updateCsvFieldMapping: async (mapping) => {
        await get().updateConfig({ csvFieldMapping: mapping });
      },

      // ==========================================================================
      // LOGGING
      // ==========================================================================

      updateLoggingConfig: async (loggingConfig) => {
        const state = get();
        const newLogging = {
          ...state.config.logging,
          ...loggingConfig,
          categories: loggingConfig.categories
            ? { ...state.config.logging.categories, ...loggingConfig.categories }
            : state.config.logging.categories,
        };

        // Sync with logger instance
        logger.setConfig(newLogging);

        // Update local state
        set({
          config: {
            ...state.config,
            logging: newLogging,
            updatedAt: new Date().toISOString(),
          },
        });

        // Persist to database
        await configService.update({ logging: newLogging });
      },

      setLoggingEnabled: async (enabled) => {
        await get().updateLoggingConfig({ enabled });
      },

      setLogLevel: async (level) => {
        await get().updateLoggingConfig({ level });
      },

      toggleLoggingCategory: async (category) => {
        const state = get();
        const newCategories = {
          ...state.config.logging.categories,
          [category]: !state.config.logging.categories[category],
        };

        const newLogging = {
          ...state.config.logging,
          categories: newCategories,
        };

        // Sync with logger instance
        logger.setConfig(newLogging);

        // Update local state
        set({
          config: {
            ...state.config,
            logging: newLogging,
            updatedAt: new Date().toISOString(),
          },
        });

        // Persist to database
        await configService.update({ logging: newLogging });
      },

      resetLoggingConfig: async () => {
        // Sync with logger instance
        logger.setConfig(DEFAULT_LOGGING_CONFIG);

        // Update local state
        set((state) => ({
          config: {
            ...state.config,
            logging: DEFAULT_LOGGING_CONFIG,
            updatedAt: new Date().toISOString(),
          },
        }));

        // Persist to database
        await configService.update({ logging: DEFAULT_LOGGING_CONFIG });
      },

      // ==========================================================================
      // CREDENTIALS
      // ==========================================================================

      updateCredentials: async (credentialsUpdate) => {
        const state = get();
        const newCredentials = {
          ...state.config.credentials,
          ...credentialsUpdate,
        };

        // Update local state
        set({
          config: {
            ...state.config,
            credentials: newCredentials,
            updatedAt: new Date().toISOString(),
          },
        });

        // Persist to database
        await configService.update({ credentials: newCredentials });
      },

      setAhrefsApiKey: async (key) => {
        await get().updateCredentials({ ahrefsApiKey: key });
      },

      setClaudeApiKey: async (key) => {
        await get().updateCredentials({ claudeApiKey: key });
      },

      setGaPropertyId: async (propertyId) => {
        await get().updateCredentials({ gaPropertyId: propertyId });
      },

      setGscSiteUrl: async (siteUrl) => {
        await get().updateCredentials({ gscSiteUrl: siteUrl });
      },

      clearAllCredentials: async () => {
        await get().updateCredentials(DEFAULT_CREDENTIALS);
      },

      // ==========================================================================
      // LOADING/ERROR
      // ==========================================================================

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error }),
    }),
    {
      name: 'content-manager-config',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        config: state.config,
      }),
      onRehydrateStorage: () => (state) => {
        // Ensure logging config exists (migration for existing users)
        if (state?.config && !state.config.logging) {
          state.config.logging = DEFAULT_LOGGING_CONFIG;
        }
        // Ensure credentials config exists (migration for existing users)
        if (state?.config && !state.config.credentials) {
          state.config.credentials = DEFAULT_CREDENTIALS;
        }
        // Sync logger with persisted config when store is rehydrated
        if (state?.config?.logging) {
          logger.setConfig(state.config.logging);
        }
      },
    }
  )
);

// =============================================================================
// SELECTORS
// =============================================================================

export const selectConfig = (state: ConfigState) => state.config;
export const selectConfigLoading = (state: ConfigState) => state.isLoading;
export const selectConfigError = (state: ConfigState) => state.error;
export const selectAccentColour = (state: ConfigState) => state.config.accentColour;
export const selectTheme = (state: ConfigState) => state.config.theme;
export const selectHidePerformanceTab = (state: ConfigState) =>
  state.config.hidePerformanceTab;
export const selectContentTypes = (state: ConfigState) => state.config.contentTypes;
export const selectPlatforms = (state: ConfigState) => state.config.platforms;
export const selectContentSilos = (state: ConfigState) => state.config.contentSilos;
export const selectPlatformBrands = (state: ConfigState) => state.config.platformBrands;
export const selectCsvFieldMapping = (state: ConfigState) => state.config.csvFieldMapping;
export const selectLoggingConfig = (state: ConfigState) => state.config.logging ?? DEFAULT_LOGGING_CONFIG;
export const selectLoggingEnabled = (state: ConfigState) => state.config.logging?.enabled ?? false;
export const selectLogLevel = (state: ConfigState) => state.config.logging?.level ?? 'INFO';

// Credentials selectors
export const selectCredentials = (state: ConfigState) => state.config.credentials ?? DEFAULT_CREDENTIALS;
export const selectAhrefsApiKey = (state: ConfigState) => state.config.credentials?.ahrefsApiKey ?? null;
export const selectClaudeApiKey = (state: ConfigState) => state.config.credentials?.claudeApiKey ?? null;
export const selectGaPropertyId = (state: ConfigState) => state.config.credentials?.gaPropertyId ?? null;
export const selectGscSiteUrl = (state: ConfigState) => state.config.credentials?.gscSiteUrl ?? null;

// Get platform colour
export const selectPlatformColour = (platform: string) => (state: ConfigState) =>
  state.config.platformBrands[platform]?.colour || '#6b7280';

export default useConfigStore;
